import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import { DARK_TABLE_BORDER } from 'constants/dark-tokens'
import OrgTeamSwitcher from 'components/DashboardBanners/OrgTeamSwitcher'
import type { SwitcherOrg, SwitcherTeam } from 'components/DashboardBanners/OrgTeamSwitcher/use-org-team-tree'
import type { ReactNode } from 'react'

// Desktop header of the dashboard: greeting and streak on the left, the role's
// banner on the right, scope switcher underneath.
//
// Desktop only — MobileShell keeps its existing stacked greeting, and the banner
// is a large-screen feature. The banner itself arrives as a node so this file does
// not need to know whether the viewer is a player or a coach.

interface IBadgeTheme {
  bg: string
  color: string
}

interface IDashboardHeaderProps {
  greetingName: string
  roleLabel: string
  badgeTheme: IBadgeTheme
  /** PlayerBanner for an athlete, OrgTeamBanner for a coach. */
  banner: ReactNode
  streak: ReactNode
  organizations: SwitcherOrg[]
  selectedOrgId: number | null
  onOrgChange: (orgId: number | null) => void
  teams: SwitcherTeam[]
  selectedTeamId: number | null
  onTeamChange: (teamId: number | null) => void
  isSwitcherLoading?: boolean
}

const DashboardHeader = ({
  greetingName,
  roleLabel,
  badgeTheme,
  banner,
  streak,
  organizations,
  selectedOrgId,
  onOrgChange,
  teams,
  selectedTeamId,
  onTeamChange,
  isSwitcherLoading
}: IDashboardHeaderProps) => {
  return (
    // Gaps are declared per child rather than through a shared `spacing`, because
    // the banner→switcher and switcher→rule distances are tuned separately.
    <VStack align="stretch" spacing={0}>
      <HStack spacing="24px" align="center">
        {/* Greeting column. minW=0 so a long name shortens itself instead of
            squeezing the banner. */}
        <VStack align="flex-start" spacing="12px" flex="0 1 34%" minW={0}>
          <HStack spacing="10px" align="center" maxW="100%">
            <Text color="#F2F7FA" fontSize="20px" fontWeight={700} lineHeight="28px" m={0} noOfLines={1}>
              Welcome, {greetingName}!
            </Text>
            <Box
              px="10px"
              h="22px"
              borderRadius="6px"
              bg={badgeTheme.bg}
              display="inline-flex"
              alignItems="center"
              flexShrink={0}
            >
              <Text fontSize="10px" fontWeight={700} color={badgeTheme.color} m={0} letterSpacing="0.10em">
                {roleLabel}
              </Text>
            </Box>
          </HStack>
          {streak}
        </VStack>

        <Box flex="1 1 66%" minW={0}>
          {banner}
        </Box>
      </HStack>

      {/* Design says 8px; opened up on review because the banner's outer glow eats
          into the gap and made the switcher look stuck to it. */}
      <Box pt="14px">
        <OrgTeamSwitcher
          organizations={organizations}
          selectedOrgId={selectedOrgId}
          onOrgChange={onOrgChange}
          teams={teams}
          selectedTeamId={selectedTeamId}
          onTeamChange={onTeamChange}
          isLoading={isSwitcherLoading}
        />
      </Box>

      {/* Closes the header off from whatever follows — the organization banner, or
          Quick Stats when no organization is selected.

          Full-bleed: the rule runs from the sidebar to the right edge of the
          window, so it cancels DesktopShell's horizontal padding with a matching
          negative margin. Those two values must stay in step — changing `px` on
          DesktopShell without changing this leaves the rule short or overhanging. */}
      <Box mt="16px" mx={-4} h="1px" bg={DARK_TABLE_BORDER} />
    </VStack>
  )
}

export default DashboardHeader
